import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Layers3, Sparkles, Gem, Crown, ScanLine } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";

const METRICS = [
  { key: "value", label: "Most valuable", icon: Gem },
  { key: "cards", label: "Most cards", icon: Layers3 },
  { key: "unique", label: "Unique cards", icon: Sparkles },
  { key: "top_card", label: "Top card", icon: Crown },
];

const RANK_STYLES = {
  1: "bg-[#F59E0B]/15 border-[#F59E0B]/50 text-[#F59E0B]",
  2: "bg-zinc-300/10 border-zinc-300/40 text-zinc-300",
  3: "bg-orange-700/15 border-orange-500/40 text-orange-400",
};

export default function Leaderboard() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [metric, setMetric] = useState("value");
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/community/leaderboard", { params: { metric } })
      .then((res) => live && setData(res.data))
      .catch(() => live && setData(null));
    return () => { live = false; };
  }, [metric]);

  const mainStat = (row) => {
    if (metric === "value") return format(row.total_value) ?? "$0.00";
    if (metric === "cards") return `${row.total_cards} cards`;
    if (metric === "unique") return `${row.unique_cards} unique`;
    return row.top_card ? format(row.top_card.price) : "—";
  };

  const subStat = (row) => {
    if (metric === "top_card" && row.top_card) {
      return [row.top_card.name, row.top_card.set_name].filter(Boolean).join(" · ");
    }
    return `${row.total_cards} cards · ${format(row.total_value) ?? "$0.00"} value`;
  };

  return (
    <section data-testid="leaderboard-section" className="border border-white/10 bg-[#111113] rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#F59E0B]" /> Collector Leaderboard
        </h2>
        <div className="flex gap-1.5 flex-wrap">
          {METRICS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`leaderboard-metric-${key}`}
              onClick={() => setMetric(key)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${metric === key ? "border-[#F59E0B]/60 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"}`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        The biggest public binders on CardArk — ranked by real market prices, not wishful thinking. Scan your collection to climb.
      </p>

      {!data ? (
        <div className="py-10 text-center"><div className="w-6 h-6 border-2 border-white/20 border-t-[#F59E0B] rounded-full animate-spin mx-auto" /></div>
      ) : (
        <>
          <div className="space-y-1.5">
            {data.leaders.map((row) => (
              <Link
                key={row.user_id}
                to={`/collector/${row.user_id}`}
                data-testid={`leaderboard-row-${row.user_id}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors hover:border-[#F59E0B]/40 ${row.user_id === user?.user_id ? "border-[#A78BFA]/50 bg-[#A78BFA]/5" : "border-white/5 bg-black/20"}`}
              >
                <span className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center text-sm font-display font-bold ${RANK_STYLES[row.rank] || "border-white/10 text-zinc-500"}`}>
                  {row.rank}
                </span>
                {metric === "top_card" && row.top_card?.image_url && (
                  <img
                    src={pcImageUrl(row.top_card.image_url, "240")}
                    onError={pcImageFallback}
                    alt={row.top_card.name}
                    className="w-8 h-11 object-cover rounded border border-white/10 shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{row.display_name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{subStat(row)}</p>
                </div>
                <span data-testid={`leaderboard-stat-${row.user_id}`} className="font-mono-plex text-sm font-semibold text-[#F59E0B] shrink-0">
                  {mainStat(row)}
                </span>
              </Link>
            ))}
            {data.leaders.length === 0 && (
              <p className="text-sm text-zinc-500 py-6 text-center">No public binders yet — be the first on the board.</p>
            )}
          </div>

          {user && (
            data.me?.rank != null ? (
              data.me.rank > 10 && (
                <div data-testid="leaderboard-me" className="mt-3 flex items-center gap-3 rounded-xl px-3 py-2.5 border border-[#A78BFA]/40 bg-[#A78BFA]/5">
                  <span className="w-8 h-8 shrink-0 rounded-full border border-[#A78BFA]/40 flex items-center justify-center text-xs font-display font-bold text-[#A78BFA]">
                    {data.me.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">You — {data.me.display_name}</p>
                    <p className="text-[11px] text-zinc-500">{subStat(data.me)}</p>
                  </div>
                  <Link to="/upload" className="text-xs font-medium px-3 py-1.5 rounded-full border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors shrink-0">
                    <ScanLine className="w-3 h-3 inline mr-1" /> Scan to climb
                  </Link>
                </div>
              )
            ) : (
              <div data-testid="leaderboard-me-cta" className="mt-3 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 border border-dashed border-white/15">
                <p className="text-xs text-zinc-400">You're not on the board yet — scan your collection (and keep your binder public) to claim a spot.</p>
                <Link to="/upload" className="text-xs font-medium px-3 py-1.5 rounded-full border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors shrink-0">
                  Scan cards
                </Link>
              </div>
            )
          )}
        </>
      )}
    </section>
  );
}
